import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';
import { Search, Filter, Database, Link, Unlink, CheckCircle, Clock, Circle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ t, userRole }) => {
  const isAdmin = userRole === 'administrator';
  const [adminTab, setAdminTab] = useState('users');
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Workouts Explorer state
  const [showWorkoutsExplorer, setShowWorkoutsExplorer] = useState(false);
  const [workoutsData, setWorkoutsData] = useState(null);
  const [workoutsLoading, setWorkoutsLoading] = useState(false);
  const [workoutsFilters, setWorkoutsFilters] = useState({
    status: '',
    linked: '',
    client_id: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    if (adminTab === 'users') {
      fetchAllUsers();
    } else if (adminTab === 'stats') {
      fetchStats();
    } else if (adminTab === 'logs') {
      fetchAuditLogs();
    }
  }, [adminTab]);

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users/all`);
      setAllUsers(res.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  // Compute dynamic seniority based on signup date
  // Older signup = lower number (more senior)
  const usersWithSeniority = useMemo(() => {
    if (!allUsers.length) return [];
    
    // Sort by created_at ascending (oldest first)
    const sorted = [...allUsers].sort((a, b) => {
      const dateA = new Date(a.created_at || '9999-12-31');
      const dateB = new Date(b.created_at || '9999-12-31');
      return dateA - dateB;
    });
    
    // Assign seniority numbers
    return sorted.map((user, index) => ({
      ...user,
      seniority: index + 1
    }));
  }, [allUsers]);

  // Format date as DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      alert('Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/audit-logs`);
      setAuditLogs(res.data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      alert('Error loading audit logs');
    } finally {
      setLoading(false);
    }
  };

  // Workouts Explorer fetch
  const fetchWorkoutsExplorer = useCallback(async () => {
    try {
      setWorkoutsLoading(true);
      const params = new URLSearchParams();
      if (workoutsFilters.status) params.append('status', workoutsFilters.status);
      if (workoutsFilters.linked) params.append('linked', workoutsFilters.linked);
      if (workoutsFilters.client_id) params.append('client_id', workoutsFilters.client_id);
      if (workoutsFilters.date_from) params.append('date_from', workoutsFilters.date_from);
      if (workoutsFilters.date_to) params.append('date_to', workoutsFilters.date_to);
      
      const res = await axios.get(`${API}/admin/workouts-explorer?${params.toString()}`);
      setWorkoutsData(res.data);
    } catch (error) {
      console.error('Error fetching workouts:', error);
      alert('Error loading workouts explorer');
    } finally {
      setWorkoutsLoading(false);
    }
  }, [workoutsFilters]);

  // Load workouts when explorer opens or filters change
  useEffect(() => {
    if (showWorkoutsExplorer) {
      fetchWorkoutsExplorer();
    }
  }, [showWorkoutsExplorer, fetchWorkoutsExplorer]);

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`);
      alert('✅ User deleted successfully');
      fetchAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('❌ Error deleting user');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus ? 'deactivate' : 'activate';
      await axios.put(`${API}/admin/users/${userId}/${endpoint}`);
      alert(`✅ User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchAllUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('❌ Error updating user status');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/users/${resetPasswordUser.id}/reset-password`, {
        new_password: newPassword
      });
      alert('✅ Password reset successfully');
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('❌ Error resetting password');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">🔧 System</h2>
          <p className="text-blue-300 text-sm mt-1">Accounts, platform & security</p>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="flex space-x-3 border-b border-blue-400/20 pb-3 overflow-x-auto">
        <button
          onClick={() => setAdminTab('users')}
          data-testid="system-subtab-users"
          className={`px-4 py-2 rounded whitespace-nowrap ${
            adminTab === 'users' ? 'bg-purple-500/30 text-purple-200' : 'text-blue-300 hover:bg-blue-900/30'
          }`}
        >
          👥 User Accounts
        </button>
        <button
          onClick={() => setAdminTab('stats')}
          data-testid="system-subtab-stats"
          className={`px-4 py-2 rounded whitespace-nowrap ${
            adminTab === 'stats' ? 'bg-purple-500/30 text-purple-200' : 'text-blue-300 hover:bg-blue-900/30'
          }`}
        >
          📊 Platform Stats
        </button>
        <button
          onClick={() => setAdminTab('logs')}
          data-testid="system-subtab-logs"
          className={`px-4 py-2 rounded whitespace-nowrap ${
            adminTab === 'logs' ? 'bg-purple-500/30 text-purple-200' : 'text-blue-300 hover:bg-blue-900/30'
          }`}
        >
          📋 Audit Logs
        </button>
      </div>

      {loading && <div className="text-center text-blue-300 py-8">Loading...</div>}

      {/* Users Tab */}
      {adminTab === 'users' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-white">User Accounts ({usersWithSeniority.length})</h3>
              <p className="text-blue-400 text-xs mt-1">Manage all system users: administrators, trainers, and clients</p>
            </div>
            <button onClick={fetchAllUsers} className="btn-secondary text-sm">
              🔄 Refresh
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-900/30">
                <tr>
                  <th className="px-4 py-3 text-left text-blue-200">Name</th>
                  <th className="px-4 py-3 text-left text-blue-200">Email</th>
                  <th className="px-4 py-3 text-left text-blue-200">Role</th>
                  <th className="px-4 py-3 text-left text-blue-200">Status</th>
                  <th className="px-4 py-3 text-left text-blue-200">Signup Date</th>
                  <th className="px-4 py-3 text-left text-blue-200">Seniority</th>
                  <th className="px-4 py-3 text-right text-blue-200">Security Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersWithSeniority.map((user) => (
                  <tr key={user.id} className="border-b border-blue-900/20 hover:bg-blue-900/20">
                    <td className="px-4 py-3 text-white">{user.full_name}</td>
                    <td className="px-4 py-3 text-blue-300">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${
                        user.role === 'administrator' ? 'bg-purple-500/20 text-purple-200' :
                        user.role === 'trainer' ? 'bg-green-500/20 text-green-200' :
                        'bg-blue-500/20 text-blue-200'
                      }`}>
                        {user.role === 'administrator' ? '👑' : user.role === 'trainer' ? '👨‍🏫' : '👤'} {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${
                        user.is_active !== false ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'
                      }`}>
                        {user.is_active !== false ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-blue-300 text-sm">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-blue-500/20 text-blue-200 px-2 py-1 rounded text-sm font-medium">
                        #{user.seniority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setResetPasswordUser(user)}
                          className="text-yellow-400 hover:text-yellow-300 text-xs px-2 py-1 border border-yellow-400/30 rounded"
                        >
                          🔑 {t('reset_password')}
                        </button>
                        <button
                          onClick={() => handleToggleActive(user.id, user.is_active !== false)}
                          className={`text-xs px-2 py-1 border rounded ${
                            user.is_active !== false 
                              ? 'text-orange-400 hover:text-orange-300 border-orange-400/30'
                              : 'text-green-400 hover:text-green-300 border-green-400/30'
                          }`}
                        >
                          {user.is_active !== false ? `⏸️ ${t('deactivate')}` : `▶️ ${t('activate')}`}
                        </button>
                        {user.role !== 'administrator' && (
                          <button
                            onClick={() => handleDeleteUser(user.id, user.full_name)}
                            className="text-red-400 hover:text-red-300 text-xs px-2 py-1 border border-red-400/30 rounded"
                          >
                            🗑️ {t('delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {adminTab === 'stats' && !loading && stats && (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-white">Platform Statistics</h3>
            <p className="text-blue-400 text-xs mt-1">System-wide metrics and resource usage</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card p-6">
              <div className="text-blue-300 text-sm mb-2">👥 Total Users</div>
              <div className="text-3xl font-bold text-white">{stats.total_users}</div>
              <div className="text-xs text-blue-400 mt-2">
                👑 {stats.total_administrators} • 👨‍🏫 {stats.total_trainers} • 👤 {stats.total_clients}
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="text-blue-300 text-sm mb-2">💪 Total Workouts</div>
              <div className="text-3xl font-bold text-white">{stats.total_workouts}</div>
              <div className="text-xs text-green-400 mt-2">
                ✅ {stats.completed_workouts} completed
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="text-blue-300 text-sm mb-2">🏋️ Exercises</div>
              <div className="text-3xl font-bold text-white">{stats.total_exercises}</div>
              <div className="text-xs text-blue-400 mt-2">
                📋 {stats.total_templates} templates
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="text-blue-300 text-sm mb-2">📅 Bookings</div>
              <div className="text-3xl font-bold text-white">{stats.total_bookings}</div>
              <div className="text-xs text-blue-400 mt-2">
                ⏰ {stats.active_timeslots || 0} / {stats.total_timeslots || 0} slots available
              </div>
            </div>
          </div>

          {/* Workout Breakdown Card */}
          {stats.workout_breakdown && (
            <div className="glass-card p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-400" />
                  Workout Data Breakdown
                </h4>
                <button
                  onClick={() => setShowWorkoutsExplorer(true)}
                  className="btn-secondary text-sm flex items-center gap-2"
                  data-testid="open-workouts-explorer-btn"
                >
                  <Search className="w-4 h-4" />
                  View Workouts Explorer
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-blue-900/30 rounded-lg p-4 text-center">
                  <div className="text-blue-300 text-xs mb-1">Total</div>
                  <div className="text-2xl font-bold text-white">{stats.workout_breakdown.total}</div>
                </div>
                <div className="bg-green-900/30 rounded-lg p-4 text-center">
                  <div className="text-green-300 text-xs mb-1 flex items-center justify-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Finished
                  </div>
                  <div className="text-2xl font-bold text-green-400">{stats.workout_breakdown.finished}</div>
                </div>
                <div className="bg-yellow-900/30 rounded-lg p-4 text-center">
                  <div className="text-yellow-300 text-xs mb-1 flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" /> In Progress
                  </div>
                  <div className="text-2xl font-bold text-yellow-400">{stats.workout_breakdown.in_progress}</div>
                </div>
                <div className="bg-gray-700/30 rounded-lg p-4 text-center">
                  <div className="text-gray-300 text-xs mb-1 flex items-center justify-center gap-1">
                    <Circle className="w-3 h-3" /> Not Started
                  </div>
                  <div className="text-2xl font-bold text-gray-400">{stats.workout_breakdown.not_started}</div>
                </div>
                <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                  <div className="text-purple-300 text-xs mb-1 flex items-center justify-center gap-1">
                    <Link className="w-3 h-3" /> Linked
                  </div>
                  <div className="text-2xl font-bold text-purple-400">{stats.workout_breakdown.linked}</div>
                </div>
                <div className="bg-red-900/30 rounded-lg p-4 text-center">
                  <div className="text-red-300 text-xs mb-1 flex items-center justify-center gap-1">
                    <Unlink className="w-3 h-3" /> Unlinked
                  </div>
                  <div className="text-2xl font-bold text-red-400">{stats.workout_breakdown.unlinked}</div>
                </div>
              </div>
              
              <p className="text-blue-400 text-xs mt-4">
                ℹ️ Unlinked workouts have no associated booking (booking_id = null). These may be orphaned data from deleted bookings.
              </p>
            </div>
          )}

          <div className="glass-card p-6">
            <h4 className="text-lg font-semibold text-white mb-4">📈 Recent Activity (Last 7 Days)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-blue-300 text-sm">New Users</div>
                <div className="text-2xl font-bold text-green-400">{stats.recent_users}</div>
              </div>
              <div>
                <div className="text-blue-300 text-sm">New Workouts</div>
                <div className="text-2xl font-bold text-green-400">{stats.recent_workouts}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workouts Explorer Modal */}
      {showWorkoutsExplorer && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-400" />
                  Workouts Explorer
                </h3>
                <p className="text-sm text-gray-400">Audit and explore workout data</p>
              </div>
              <button
                onClick={() => setShowWorkoutsExplorer(false)}
                className="text-gray-400 hover:text-white p-2"
              >
                ✕
              </button>
            </div>
            
            {/* Filters */}
            <div className="p-4 border-b border-gray-700 flex-shrink-0">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="min-w-[140px]">
                  <label className="block text-xs text-gray-400 mb-1">Status</label>
                  <select
                    value={workoutsFilters.status}
                    onChange={(e) => setWorkoutsFilters(f => ({ ...f, status: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    data-testid="workouts-filter-status"
                  >
                    <option value="">All Status</option>
                    <option value="finished">Finished</option>
                    <option value="in_progress">In Progress</option>
                    <option value="not_started">Not Started</option>
                  </select>
                </div>
                
                <div className="min-w-[140px]">
                  <label className="block text-xs text-gray-400 mb-1">Linked</label>
                  <select
                    value={workoutsFilters.linked}
                    onChange={(e) => setWorkoutsFilters(f => ({ ...f, linked: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    data-testid="workouts-filter-linked"
                  >
                    <option value="">All</option>
                    <option value="linked">Linked (has booking)</option>
                    <option value="unlinked">Unlinked (orphaned)</option>
                  </select>
                </div>
                
                <div className="min-w-[180px]">
                  <label className="block text-xs text-gray-400 mb-1">Client</label>
                  <select
                    value={workoutsFilters.client_id}
                    onChange={(e) => setWorkoutsFilters(f => ({ ...f, client_id: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    data-testid="workouts-filter-client"
                  >
                    <option value="">All Clients</option>
                    {workoutsData?.available_clients?.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="min-w-[140px]">
                  <label className="block text-xs text-gray-400 mb-1">Date From</label>
                  <input
                    type="date"
                    value={workoutsFilters.date_from}
                    onChange={(e) => setWorkoutsFilters(f => ({ ...f, date_from: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  />
                </div>
                
                <div className="min-w-[140px]">
                  <label className="block text-xs text-gray-400 mb-1">Date To</label>
                  <input
                    type="date"
                    value={workoutsFilters.date_to}
                    onChange={(e) => setWorkoutsFilters(f => ({ ...f, date_to: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  />
                </div>
                
                <button
                  onClick={() => setWorkoutsFilters({ status: '', linked: '', client_id: '', date_from: '', date_to: '' })}
                  className="px-3 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Clear Filters
                </button>
              </div>
            </div>
            
            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {workoutsLoading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : (
                <>
                  <div className="text-sm text-gray-400 mb-3">
                    Showing {workoutsData?.total || 0} workouts
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-700/50">
                        <tr>
                          <th className="px-3 py-2 text-left text-gray-300">Date</th>
                          <th className="px-3 py-2 text-left text-gray-300">Client</th>
                          <th className="px-3 py-2 text-left text-gray-300">Status</th>
                          <th className="px-3 py-2 text-center text-gray-300">Exercises</th>
                          <th className="px-3 py-2 text-left text-gray-300">Booking</th>
                          <th className="px-3 py-2 text-left text-gray-300">Workout ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workoutsData?.workouts?.map((w) => (
                          <tr key={w.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                            <td className="px-3 py-2 text-white">{w.date || '-'}</td>
                            <td className="px-3 py-2 text-white">{w.client_name}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                                w.status === 'finished' ? 'bg-green-500/20 text-green-300' :
                                w.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-300' :
                                'bg-gray-500/20 text-gray-300'
                              }`}>
                                {w.status === 'finished' && <CheckCircle className="w-3 h-3" />}
                                {w.status === 'in_progress' && <Clock className="w-3 h-3" />}
                                {w.status === 'not_started' && <Circle className="w-3 h-3" />}
                                {w.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center text-gray-300">{w.exercise_count}</td>
                            <td className="px-3 py-2">
                              {w.has_booking ? (
                                <span className="inline-flex items-center gap-1 text-purple-300">
                                  <Link className="w-3 h-3" />
                                  {w.booking_date} {w.booking_time}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-400">
                                  <Unlink className="w-3 h-3" />
                                  Unlinked
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500 font-mono text-xs">
                              {w.id?.substring(0, 8)}...
                            </td>
                          </tr>
                        ))}
                        {(!workoutsData?.workouts || workoutsData.workouts.length === 0) && (
                          <tr>
                            <td colSpan="6" className="px-3 py-8 text-center text-gray-500">
                              No workouts found matching filters
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audit Logs Tab */}
      {adminTab === 'logs' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-white">Audit Logs ({auditLogs.length})</h3>
              <p className="text-blue-400 text-xs mt-1">System activity and security events</p>
            </div>
            <button onClick={fetchAuditLogs} className="btn-secondary text-sm">
              🔄 Refresh
            </button>
          </div>

          {auditLogs.length === 0 ? (
            <div className="text-center text-blue-300 py-8">No audit logs recorded yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-900/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-blue-200">Timestamp</th>
                    <th className="px-4 py-3 text-left text-blue-200">User</th>
                    <th className="px-4 py-3 text-left text-blue-200">Action</th>
                    <th className="px-4 py-3 text-left text-blue-200">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, index) => (
                    <tr key={index} className="border-b border-blue-900/20">
                      <td className="px-4 py-3 text-blue-300 text-sm">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-white">{log.user_name}</td>
                      <td className="px-4 py-3">
                        <span className="badge bg-purple-500/20 text-purple-200">{log.action}</span>
                      </td>
                      <td className="px-4 py-3 text-blue-300 text-sm">
                        {JSON.stringify(log.details)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <Modal onClose={() => { setResetPasswordUser(null); setNewPassword(''); }} maxWidth="max-w-md">
          <Modal.Header onClose={() => { setResetPasswordUser(null); setNewPassword(''); }}>
            <h3 className="text-xl font-semibold text-white">
              {t('reset_password')} - {resetPasswordUser.full_name}
            </h3>
          </Modal.Header>
          
          <Modal.Body>
            <div className="space-y-4">
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  {t('new_password')}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
            </div>
          </Modal.Body>
          
          <Modal.Footer className="flex space-x-3">
            <button
              onClick={() => {
                setResetPasswordUser(null);
                setNewPassword('');
              }}
              className="btn-secondary flex-1"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleResetPassword}
              className="btn-primary flex-1"
            >
              {t('reset_password')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default AdminPanel;
